import { useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from '@s20/hooks/redux.hooks';
import { getPermissionStrings } from '@s20/selectors/features/permissions.selectors';
import { useSignalR } from '@s20/hooks/signalr.hooks';
import { getAllTenants, getUser, getUserId } from '@s20/selectors/features/common.selectors';
import { ApplicationPermissions } from '../../Common';
import { NotificationToast } from './NotificationToast';
import { NotificationTypes } from './Notification';

const message = ({ toastId, ...payload }) => toast.info(
    <NotificationToast {...payload} />,
    {
        toastId,
        position: 'top-right',
        pauseOnFocusLoss: false,
        closeButton: false,
    },
);

export const Notification = () => {
    const tenants = useSelector(getAllTenants);
    const userId = useSelector(getUserId);
    const user = useSelector(getUser);

    const permissions = useSelector(getPermissionStrings);

    const hub = useSignalR('messageHub');

    const getData = useCallback((data) => {
        if (!data) {
            return null;
        }

        try {
            const payload = JSON.parse(data);

            if (payload.createdBy === userId) {
                return null;
            }

            if (payload.tenantId && tenants.some((tenant) => tenant.id === payload.tenantId)) {
                return payload;
            }
        } catch (e) {
            console.error(e);
            return null;
        }

        return null;
    }, [tenants, userId]);

    const getNotificationHandler = useCallback((notificationType) => (rawData) => {
        const payload = getData(rawData);

        if (payload?.id) {
            message({ toastId: payload.id, notificationType, ...payload });
        }
    }, [getData]);

    useEffect(() => {
        if (hub) {
            const hasDispatchPlusNotificationPermission = permissions.includes(ApplicationPermissions.notification_dispatch_plus)
                && Boolean(user?.isSubscribedToDispatchPlusNotifications);
            const hasJobRequestViewPermission = permissions.includes(ApplicationPermissions.jobrequest_view);
            const hasJobViewPermission = permissions.includes(ApplicationPermissions.job_view);
            const hasTimesheetManagementPermission = permissions.includes(ApplicationPermissions.dispatch_review_view);
            const hasDispatchIssuesViewPermission = permissions.includes(ApplicationPermissions.dispatch_issues_view);

            if (hasDispatchPlusNotificationPermission) {
                hub.invoke('JoinGroup', 'DispatchPlusNotifications').catch((e) => console.log('Error joining group', e));

                if (hasJobRequestViewPermission) {
                    hub.on(NotificationTypes.JobRequestCreated, getNotificationHandler(NotificationTypes.JobRequestCreated));
                }

                if (hasJobViewPermission) {
                    hub.on(NotificationTypes.JobCreated, getNotificationHandler(NotificationTypes.JobCreated));
                }

                if (hasTimesheetManagementPermission) {
                    hub.on(NotificationTypes.TimesheetRejected, getNotificationHandler(NotificationTypes.TimesheetRejected));
                    hub.on(NotificationTypes.TimesheetOverEstimate, getNotificationHandler(NotificationTypes.TimesheetOverEstimate));
                }

                if (hasDispatchIssuesViewPermission) {
                    hub.on(NotificationTypes.EmployeeCallOff, getNotificationHandler(NotificationTypes.EmployeeCallOff));
                }
            }

            return () => {
                hub.off(NotificationTypes.JobRequestCreated);
                hub.off(NotificationTypes.JobCreated);
                hub.off(NotificationTypes.TimesheetRejected);
                hub.off(NotificationTypes.TimesheetOverEstimate);
                hub.off(NotificationTypes.EmployeeCallOff);
            };
        }

        return undefined;
    }, [hub, permissions, user, getNotificationHandler]);

    return null;
};